import _objectWithoutProperties from "/drone/src/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["className"];
import React, { forwardRef, Fragment, useMemo } from 'react';
import { useDataTable } from './hooks/useDataTable';
import { Table, TableHead, TableRow, TableBody, TableCheckboxCell, TableExpandableSection } from '../Table';
import { renderExpandableSectionEmpty } from './hooks/useDataTableExpandableSections';
import { getArrayOfScalarPropType, getScalarPropType } from '../../utils/propTypes';
import { DataTableRoot, DataTableVirtualPadding, DataTableFooter } from './style';
import { DATA_TABLE_CLASS_PREFIX } from './constants';
import { useComponentTheme } from '../../utils/hooks';
import { useDataTableClassNames } from './hooks/useDataTableClassNames';
import { PageNavigation } from '../PageNavigation';
import { makeRowClassName } from './utils/makeRowClassName';
import { ActionSheet } from '../ActionSheet';
import { DataTableContext } from './DataTableContext';
export var DataTable = forwardRef(function DataTable(_ref, ref) {
  var className = _ref.className,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useDataTable = useDataTable(props, ref),
      columns = _useDataTable.columns,
      data = _useDataTable.data,
      rootProps = _useDataTable.rootProps,
      tableProps = _useDataTable.tableProps,
      tableBodyProps = _useDataTable.tableBodyProps,
      getRowProps = _useDataTable.getRowProps,
      getHeadCellProps = _useDataTable.getHeadCellProps,
      getCellProps = _useDataTable.getCellProps,
      getExpandableSectionProps = _useDataTable.getExpandableSectionProps,
      getCellTag = _useDataTable.getCellTag,
      getHeadCellTag = _useDataTable.getHeadCellTag,
      enableRowSelection = _useDataTable.enableRowSelection,
      getSelectAllCheckboxCellProps = _useDataTable.getSelectAllCheckboxCellProps,
      getSelectRowCheckboxCellProps = _useDataTable.getSelectRowCheckboxCellProps,
      isActionSheetVisible = _useDataTable.isActionSheetVisible,
      actions = _useDataTable.actions,
      actionSheetProps = _useDataTable.actionSheetProps,
      selectedRowIds = _useDataTable.selectedRowIds,
      unselectRows = _useDataTable.unselectRows,
      enableExpandableSections = _useDataTable.enableExpandableSections,
      renderExpandableSection = _useDataTable.renderExpandableSection,
      enablePageNavigation = _useDataTable.enablePageNavigation,
      pageNavigationProps = _useDataTable.pageNavigationProps,
      enableVirtualization = _useDataTable.enableVirtualization,
      getVirtualTopSpaceProps = _useDataTable.getVirtualTopSpaceProps,
      getVirtualBottomSpaceProps = _useDataTable.getVirtualBottomSpaceProps,
      getRowNumber = _useDataTable.getRowNumber,
      initialDataLength = _useDataTable.initialDataLength,
      enableHeader = _useDataTable.enableHeader,
      enableFooter = _useDataTable.enableFooter;

  var theme = useComponentTheme(DATA_TABLE_CLASS_PREFIX);
  var classNameComponents = useDataTableClassNames(DATA_TABLE_CLASS_PREFIX, className);
  var contextValue = useMemo(function () {
    return {
      selectedRowIds: selectedRowIds,
      unselectRows: unselectRows,
      data: data
    };
  }, [selectedRowIds, data]);
  return React.createElement(DataTableContext.Provider, {
    value: contextValue
  }, React.createElement(DataTableRoot, Object.assign({}, rootProps, {
    theme: theme,
    className: classNameComponents.Root
  }), isActionSheetVisible && React.createElement(ActionSheet, Object.assign({
    marginBottom: 2
  }, actionSheetProps), actions), React.createElement(Table, Object.assign({}, tableProps), enableHeader && React.createElement(TableHead, null, React.createElement(TableRow, null, enableRowSelection && React.createElement(TableCheckboxCell, Object.assign({}, getSelectAllCheckboxCellProps(), {
    theme: theme
  })), columns.map(function (column) {
    var TableHeadCellComponent = getHeadCellTag(column);
    return React.createElement(TableHeadCellComponent, Object.assign({
      key: column.key || column.id
    }, getHeadCellProps(column)), column.title);
  }))), React.createElement(TableBody, Object.assign({}, tableBodyProps), enableVirtualization && React.createElement(DataTableVirtualPadding, Object.assign({}, getVirtualTopSpaceProps(), {
    className: classNameComponents.VirtualPaddingTop,
    theme: theme
  })), data.map(function (item, index) {
    var key = item.key || item.id;
    return React.createElement(Fragment, {
      key: key
    }, React.createElement(TableRow, Object.assign({}, getRowProps(item), {
      className: makeRowClassName(getRowNumber(index), initialDataLength, classNameComponents)
    }), enableRowSelection && React.createElement(TableCheckboxCell, Object.assign({}, getSelectRowCheckboxCellProps(item.id), {
      theme: theme
    })), columns.map(function (column) {
      var TableCellComponent = getCellTag(column);
      return React.createElement(TableCellComponent, Object.assign({
        key: column.id
      }, getCellProps(column, item)), column.formatDataCellContent ? column.formatDataCellContent(item[column.id], item, column) : item[column.id]);
    })), enableExpandableSections && React.createElement(TableExpandableSection, Object.assign({}, getExpandableSectionProps(item)), renderExpandableSection(item)));
  }), enableVirtualization && React.createElement(DataTableVirtualPadding, Object.assign({}, getVirtualBottomSpaceProps(), {
    className: classNameComponents.VirtualPaddingBottom,
    theme: theme
  })))), enableFooter && React.createElement(DataTableFooter, {
    theme: theme,
    className: classNameComponents.Footer
  }, enablePageNavigation && React.createElement(PageNavigation, Object.assign({}, pageNavigationProps)))));
});
DataTable.defaultProps = {
  columns: [],
  data: [],
  enableRowSelection: false,
  enableNeutralRowSorting: true,
  renderExpandableSection: renderExpandableSectionEmpty,
  enablePageNavigation: false
};